import React from 'react';
import CommonModal from '../../Components/CommonModal';
import CommonTable from '../../Components/CommonTable';
import StatusBadge from '../../Components/StatusBadge';
import { criticalityTone, formatDate, gradeTone, MISSING_TEXT, safeArray, vendorGradeFallback } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

// ─────────────────────────────────────────────────────────────
// VendorListModal — "which vendors are those?" for any number on the
// dashboard.
//
// Every figure in the dashboard's charts is a count of vendors, and a count
// you can't open is a dead end: you learn that 7 vendors can reach credential
// data but not which 7. This is the one modal every one of those numbers
// opens, so the drill-down behaves identically wherever you click.
//
// It takes an ALREADY-FILTERED list rather than a filter description. Each
// caller knows its own predicate (grade === 'C', due in March, never
// assessed…), and keeping the filtering at the call site means this component
// never has to know about any of them.
// ─────────────────────────────────────────────────────────────
function VendorListModal({ show, onHide, title, subtitle, vendors }) {
    const list = safeArray(vendors);

    // Same empty-cell vocabulary as the Vendors table — these are the same
     // columns, so they have to say the same thing when a value is missing.
    const rows = list.map((vendor) => {
        const grade = vendorGradeFallback(vendor);
        const criticality = vendor?.business_criticality;
        const name = vendor?.vendor_name || vendor?.target_organization_name;
        const service = vendor?.service_provided;
        const nextScan = formatDate(vendor?.next_assessment_scheduled_date);
        return {
            data: [
                <span className="tpcrm-cell-truncate" title={name || ''}>
                    {name || MISSING_TEXT.vendor}
                </span>,
                service
                    ? <span className="tpcrm-cell-truncate" title={service}>{service}</span>
                    : <span className="tpcrm-cell-empty">{MISSING_TEXT.service}</span>,
                criticality
                    ? <StatusBadge dot={false} style={criticalityTone(criticality)}>{criticality}</StatusBadge>
                    : <span className="tpcrm-cell-empty">{MISSING_TEXT.criticality}</span>,
                grade === '-'
                    ? <span className="tpcrm-cell-empty">{MISSING_TEXT.grade}</span>
                    : <span className="tpcrm-grade-text" style={{ color: gradeTone(grade) }}>{grade}</span>,
                nextScan === '-'
                    ? <span className="tpcrm-cell-empty">{MISSING_TEXT.nextScan}</span>
                    : nextScan,
            ],
        };
    });

    return (
        <CommonModal
            show={show}
            onHide={onHide}
            title={title || 'Vendors'}
            // The count is part of the answer, so it belongs in the subtitle
            // rather than being something you have to work out from the rows.
            subtitle={`${list.length} vendor${list.length === 1 ? '' : 's'}${subtitle ? ` · ${subtitle}` : ''}`}
            // xl, not the `mid` shorthand (which resolves to Bootstrap 'lg'):
            // five columns including a vendor name and a service description
            // need the width, and a drill-down that arrives cramped defeats the
            // point of opening it.
            size="xl"
        >
            {list.length === 0 ? (
                // Reached by clicking a zero — a real answer, so it gets a real
                // empty state rather than CommonTable's generic "No data found".
                // Reuses the portal's existing empty-state block.
                <div className="tpcrm-profile-empty">
                    <div className="tpcrm-profile-empty-title">No vendors found</div>
                    <p className="tpcrm-profile-empty-hint">
                        {subtitle
                            ? `Nothing matches "${subtitle}" right now.`
                            : 'Nothing matches this selection right now.'}
                    </p>
                </div>
            ) : (
                <div className="tpcrm-profile-table-wrap">
                    <CommonTable
                        headers={['Vendor', 'Service', 'Criticality', 'Grade', 'Next Scan']}
                        rows={rows}
                        noPadding
                        noScroll
                    />
                </div>
            )}
        </CommonModal>
    );
}

export default VendorListModal;
